// Scope/Pre-Sales Types

export interface PortalAccessUser {
  id: number
  name: string
  email: string
}

export type PresalesScopeStatus = 'draft' | 'sent' | 'adjust' | 'confirm' | 'handover' | 'implementations'

export type PresalesScopeStatusDates = Record<PresalesScopeStatus, string | null>

export type CustomWorkCategory =
  | 'report'
  | 'portal'
  | 'kyc'
  | 'idv'
  | 'onesdk'
  | 'kyb'
  | 'txm'
  | 'fraud'
  | 'risk'
  | 'api'
  | 'other'

export type JiraTicketStatus = 'tbd' | 'todo' | 'in_progress' | 'blocked' | 'done'

export type CustomWorkLifecycleStatus = 'draft' | 'negotiation' | 'confirmed' | 'on_hold' | 'done'

export type PmApprovalStatus = 'not_requested' | 'pending' | 'approved' | 'rejected'

export interface CustomWorkJiraTicket {
  id: number
  ticket: string
  status: JiraTicketStatus
}

export interface CustomWorkItem {
  id: number
  name: string
  description: string
  category: CustomWorkCategory
  dueDate: string
  effortDays: number | null
  lifecycleStatus: CustomWorkLifecycleStatus
  jiraTickets: CustomWorkJiraTicket[]
  assignedPmId: number | null  // Product manager assigned to this custom work item (FK to product_managers table)
  pmApprovalStatus: PmApprovalStatus  // Whether the product manager has signed off on this custom work
  tasksLinked?: boolean  // True when presales tasks have been created for this item
  isHardDate?: boolean   // True when this is a firm client commitment date
}

export interface PresalesActionItem {
  id: string
  completed: boolean
  completedAt: string | null
  notRequired: boolean
  notes: string
}

export type PresalesActionGroup = 'presales' | 'implementations'

export interface PresalesActionDef {
  id: string
  label: string
  group: PresalesActionGroup
  description?: string
  /**
   * When set, this action acts as a radio with the other ids in the group.
   * Ticking one unticks the others — used for Sandbox / UAT mutual exclusivity.
   */
  mutexGroup?: string
}

export const PRESALES_ACTIONS: PresalesActionDef[] = [
  // ── Presales (visualised on the stepper) ──────────────────────────────────
  { id: 'sandbox', label: 'Sandbox Access Sent', group: 'presales', mutexGroup: 'access', description: 'Sandbox access provided to the customer (alternative to UAT).' },
  { id: 'uat', label: 'UAT Access Sent', group: 'presales', mutexGroup: 'access', description: 'UAT access provided to the customer (alternative to Sandbox).' },
  { id: 'help_doc_sent', label: 'Help Doc Sent', group: 'presales', description: 'Help documentation shared with the customer.' },
  { id: 'pipeline_products_added', label: 'Added to Pipelines & Products', group: 'presales', description: 'Deal added to the internal P&P review list.' },
  { id: 'pipeline_products_discussed', label: 'Discussed in Pipelines & Products', group: 'presales', description: 'Reviewed in a P&P meeting.' },
  { id: 'pipeline_products_confirmed_standard', label: 'Confirmed Standard in P&P', group: 'presales', description: 'P&P confirmed the scope is standard (no custom work needed).' },
  { id: 'implementations_reviewing_sow', label: 'SOW with Implementations', group: 'presales', description: 'SOW handed to Implementations for internal review before sending to the customer.' },
  { id: 'scope_confirmed', label: 'Scope Signed', group: 'presales', description: 'Customer returned a signed SOW.' },

  // ── Implementations (listed, not in stepper) ──────────────────────────────
  { id: 'uat_created', label: 'UAT Created', group: 'implementations', description: 'UAT environment provisioned by Implementations.' },
  { id: 'branding_guideline_sent', label: 'Branding Guideline Sent', group: 'implementations' },
  { id: 'consent_guideline_sent', label: 'Consent Guideline Sent', group: 'implementations' },
  { id: 'sso_instructions_sent', label: 'SSO Instructions Sent', group: 'implementations' },
  { id: 'sso_instructions_received', label: 'SSO Instructions Received', group: 'implementations' },
  { id: 'dvs_forms_sent', label: 'DVS Forms Sent', group: 'implementations' },
  { id: 'postman_collection_sent', label: 'Postman Collection Sent', group: 'implementations' },
]

// Legacy action ids that no longer appear in the UI but may still exist in old scopes.
// We filter them out on render and ignore them on migration.
export const DEPRECATED_ACTION_IDS = new Set(['api_keys_sent'])

// Sources for each stage of the Presales Actions stepper visualisation.
// 'action' pulls from presalesActions; 'status' pulls from scopeStatusDates.
export type PresalesStepSource =
  | { kind: 'action'; actionIds: string[] }
  | { kind: 'status'; statusId: PresalesScopeStatus }
  | { kind: 'blueprint' }

export interface PresalesStepDef {
  id: string
  label: string
  description: string
  source: PresalesStepSource
}

export const PRESALES_STEPS: PresalesStepDef[] = [
  { id: 'blueprint', label: 'Blueprint', description: 'Architectural hypothesis captured (>0% blueprint completion).', source: { kind: 'blueprint' } },
  { id: 'access', label: 'Access Sent', description: 'Sandbox or UAT access sent to the customer.', source: { kind: 'action', actionIds: ['sandbox', 'uat'] } },
  { id: 'help_doc', label: 'Help Doc', description: 'Help documentation shared.', source: { kind: 'action', actionIds: ['help_doc_sent'] } },
  { id: 'pp_added', label: 'P&P: Added', description: 'Deal added to Pipelines & Products.', source: { kind: 'action', actionIds: ['pipeline_products_added'] } },
  { id: 'pp_discussed', label: 'P&P: Discussed', description: 'Reviewed in a P&P meeting.', source: { kind: 'action', actionIds: ['pipeline_products_discussed'] } },
  { id: 'pp_confirmed', label: 'P&P: Standard', description: 'Confirmed standard scope in P&P.', source: { kind: 'action', actionIds: ['pipeline_products_confirmed_standard'] } },
  { id: 'scope_started', label: 'Scope Started', description: 'SOW drafting began.', source: { kind: 'status', statusId: 'draft' } },
  { id: 'impl_review', label: 'SOW w/ Impl', description: 'Implementations reviewing the SOW before it goes to the customer.', source: { kind: 'action', actionIds: ['implementations_reviewing_sow'] } },
  { id: 'sow_sent', label: 'SOW Sent', description: 'SOW sent to the customer.', source: { kind: 'status', statusId: 'sent' } },
  { id: 'sow_signed', label: 'SOW Signed', description: 'Customer returned a signed SOW.', source: { kind: 'action', actionIds: ['scope_confirmed'] } },
  { id: 'handover', label: 'Handover', description: 'Deal handed over to Implementations.', source: { kind: 'status', statusId: 'handover' } },
]

// Actions shown on the operational overview dashboard
export const DASHBOARD_ACTIONS = ['sandbox', 'uat', 'help_doc_sent', 'scope_confirmed'] as const

export interface GeneralInfo {
  presalesHours: number
  productionAccessDate: string
  goLiveDate: string
  isHardGoLiveDate: boolean
  scopeStatus: PresalesScopeStatus
  scopeStatusDates: PresalesScopeStatusDates
  forecastChecks12m: number | null
  isUpsell: boolean
  scopeConfirmed: boolean
  parentChild: 'unknown' | 'parent_only' | 'parent_child' | 'standalone' | 'parent' | 'child'
  parentEntity: string
  childEntity: string

  testDetails: string

  integrationType: 'unknown' | 'api' | 'portal' | 'both'
  aiFeatures: 'yes' | 'no' | 'unknown'
  platformVersion: 'v1' | 'v2' | 'unknown'
  ssoAccess: 'yes' | 'no' | 'unknown'

  parentAccountName: string
  parentUatId: string
  parentProdId: string
  childAccountName: string
  childUatId: string
  childProdId: string

  clientUseCase: string
  phases: string[]

  sowVariationNote: string

  resellerAgreement: 'yes' | 'no' | 'unknown'
  resellerName: string
  resellerRelationshipType: 'unknown' | 'selling_to_customers' | 'customer_of_reseller'
  resellerPredefinedConfig: 'yes' | 'no' | 'unknown'
  resellerChildAccountStructure: 'unknown' | 'child_under_parent' | 'own_parent_account'

  billingRequirements: string

  portalAccessUsers: PortalAccessUser[]

  customWorks: CustomWorkItem[]

  presalesActions: PresalesActionItem[]
}

export type TriState = 'yes' | 'no' | 'unknown'

export type CreditHeaderCheckMode = TriState | 'on_behalf'

// KYC Ruleset Builder Types
export interface KycRulesetRequirement {
  dataSources: number // 0-3
  address: number // 0-3
  dateOfBirth: number // 0-3
  fullName: number // 0-3
  govId: number // 0-3
}

export interface KycRulesetDefinition {
  key: string
  label: string
  description: string
  region: 'AU' | 'NZ' | 'INTL'
  v2ConfigKey: string
  v2BundleName?: string // v2 Bundle/Workflow name
  v1RecipeName?: string // v1 Recipe name
  requirements: KycRulesetRequirement
  /** Mark as a recommended ruleset (shown with star in UI). Used to surface best-match rulesets. */
  recommended?: boolean
}

// Predefined KYC Rulesets
export const KYC_RULESETS: KycRulesetDefinition[] = [
  // Australian Rulesets
  {
    key: 'au_gov_id_only',
    label: '1+1, Gov ID (DVS only)',
    description: '1+1 with Government ID verification',
    region: 'AU',
    v2ConfigKey: 'gov_id_only',
    v2BundleName: 'AUS-Basic1V-IDOnly',
    v1RecipeName: 'gov_id',
    requirements: { dataSources: 1, address: 0, dateOfBirth: 1, fullName: 1, govId: 1 }
  },
  {
    key: 'au_one_plus_no_gov_1',
    label: '1+1 (No Gov ID) - Address + Name',
    description: '1+1 without Government ID - Address + Name',
    region: 'AU',
    v2ConfigKey: 'one_plus',
    requirements: { dataSources: 1, address: 1, dateOfBirth: 0, fullName: 1, govId: 0 }
  },
  {
    key: 'au_one_plus_no_gov_2',
    label: '1+1 (No Gov ID) - DOB + Name',
    description: '1+1 without Government ID - DOB + Name',
    region: 'AU',
    v2ConfigKey: 'one_plus',
    requirements: { dataSources: 1, address: 0, dateOfBirth: 1, fullName: 1, govId: 0 }
  },
  {
    key: 'au_one_plus_no_gov_3',
    label: '1+1 (No Gov ID) - 2×Name + 2×DOB',
    description: '1+1 without Government ID - 2×Name + 2×DOB',
    region: 'AU',
    v2ConfigKey: 'one_plus',
    requirements: { dataSources: 1, address: 0, dateOfBirth: 2, fullName: 2, govId: 0 }
  },
  {
    key: 'au_two_plus_1',
    label: '2+2 (No Gov ID) - Address + DOB + 2×Name',
    description: '2+2 without Government ID — best match rate ruleset for 2+2 verifications without Gov ID',
    region: 'AU',
    v2ConfigKey: 'two_plus',
    v2BundleName: 'AUS-Basic2V-TwoPlus',
    v1RecipeName: 'safe_harbour',
    requirements: { dataSources: 2, address: 1, dateOfBirth: 1, fullName: 2, govId: 0 },
    recommended: true,
  },
  {
    key: 'au_two_plus_2',
    label: '2+2 (No Gov ID) - 2×Address + 2×Name',
    description: '2+2 without Government ID - Focus on Address',
    region: 'AU',
    v2ConfigKey: 'two_plus',
    v2BundleName: 'AUS-Basic2V-TwoPlus',
    v1RecipeName: 'safe_harbour',
    requirements: { dataSources: 2, address: 2, dateOfBirth: 0, fullName: 2, govId: 0 }
  },
  {
    key: 'au_two_plus_3',
    label: '2+2 (No Gov ID) - 2×Address + 2×Name',
    description: '2+2 without Government ID - Balanced',
    region: 'AU',
    v2ConfigKey: 'two_plus',
    v2BundleName: 'AUS-Basic2V-TwoPlus',
    v1RecipeName: 'safe_harbour',
    requirements: { dataSources: 2, address: 2, dateOfBirth: 0, fullName: 2, govId: 0 }
  },
  {
    key: 'au_two_plus_gov_id_1',
    label: '2+2 with Gov ID - 2×DOB + 2×Name + Gov ID',
    description: '2+2 with Government ID',
    region: 'AU',
    v2ConfigKey: 'two_plus_gov_id',
    v2BundleName: 'AUS-Advanced3V-TwoPlusID',
    v1RecipeName: 'safe_harbour_id_media',
    requirements: { dataSources: 2, address: 0, dateOfBirth: 2, fullName: 2, govId: 1 }
  },
  {
    key: 'au_two_plus_gov_id_2',
    label: '2+2 with Gov ID - Address + DOB + 2×Name + Gov ID',
    description: '2+2 with Government ID + Address — best match rate ruleset for 2+2 verifications with Gov ID',
    region: 'AU',
    v2ConfigKey: 'two_plus_gov_id',
    v2BundleName: 'AUS-Advanced3V-TwoPlusID',
    v1RecipeName: 'safe_harbour_id_media',
    requirements: { dataSources: 2, address: 1, dateOfBirth: 1, fullName: 2, govId: 1 },
    recommended: true,
  },
  {
    key: 'au_two_plus_gov_id_3',
    label: '2+2 with Gov ID - 2×Address + 2×Name + Gov ID',
    description: '2+2 with Government ID - Focus on Address',
    region: 'AU',
    v2ConfigKey: 'two_plus_gov_id',
    v2BundleName: 'AUS-Advanced3V-TwoPlusID',
    v1RecipeName: 'safe_harbour_id_media',
    requirements: { dataSources: 2, address: 2, dateOfBirth: 0, fullName: 2, govId: 1 }
  },
  {
    key: 'au_two_plus_gov_id_address',
    label: '2+2 with Gov ID + Address',
    description: '2+2 with Government ID and Address verification',
    region: 'AU',
    v2ConfigKey: 'two_plus_gov_id',
    v2BundleName: 'AUS-Basic2V-TwoPlusID',
    v1RecipeName: 'safe_harbour_media',
    requirements: { dataSources: 2, address: 1, dateOfBirth: 1, fullName: 2, govId: 1 }
  },

  // New Zealand Rulesets
  {
    key: 'nz_two_plus_gov_id_address',
    label: '2+2 with Gov ID + Address (NZ)',
    description: '2+2 with Government ID and Address verification for NZ',
    region: 'NZ',
    v2ConfigKey: 'two_plus_gov_id',
    v2BundleName: 'NZL-Basic1V-OnePlusOne',
    v1RecipeName: 'international',
    requirements: { dataSources: 2, address: 1, dateOfBirth: 1, fullName: 2, govId: 1 }
  },
  {
    key: 'nz_two_plus_both',
    label: '2+2 Both Address and DOB (NZ)',
    description: '2+2 with both Address and Date of Birth',
    region: 'NZ',
    v2ConfigKey: 'two_plus_both',
    requirements: { dataSources: 2, address: 1, dateOfBirth: 1, fullName: 2, govId: 0 }
  },

  // International Rulesets
  {
    key: 'intl_one_plus_1',
    label: '1+1 International - Address + Name',
    description: '1+1 for international customers',
    region: 'INTL',
    v2ConfigKey: 'one_plus',
    v2BundleName: 'GLB-IntlOnePlus',
    v1RecipeName: 'international',
    requirements: { dataSources: 1, address: 1, dateOfBirth: 0, fullName: 1, govId: 0 }
  },
  {
    key: 'intl_one_plus_2',
    label: '1+1 International - DOB + Name',
    description: '1+1 for international customers with partial match',
    region: 'INTL',
    v2ConfigKey: 'one_plus',
    v2BundleName: 'GLB-IntlOnePlus',
    v1RecipeName: 'international',
    requirements: { dataSources: 1, address: 0, dateOfBirth: 1, fullName: 1, govId: 0 }
  },
  {
    key: 'intl_one_plus_3',
    label: '1+1 International - Name + Gov ID',
    description: '1+1 for international customers with Gov ID',
    region: 'INTL',
    v2ConfigKey: 'one_plus',
    v2BundleName: 'GLB-IntlOnePlus',
    v1RecipeName: 'international',
    requirements: { dataSources: 1, address: 0, dateOfBirth: 0, fullName: 1, govId: 1 }
  },
  {
    key: 'intl_two_plus_1',
    label: '2+2 International - 2×Address + 2×Name',
    description: '2+2 for international customers',
    region: 'INTL',
    v2ConfigKey: 'two_plus',
    requirements: { dataSources: 2, address: 2, dateOfBirth: 0, fullName: 2, govId: 0 }
  },
  {
    key: 'intl_two_plus_2',
    label: '2+2 International - Address + DOB + 2×Name',
    description: '2+2 for international customers with balanced requirements',
    region: 'INTL',
    v2ConfigKey: 'two_plus',
    requirements: { dataSources: 2, address: 1, dateOfBirth: 1, fullName: 2, govId: 0 }
  },
  {
    key: 'intl_two_plus_gov_id_1',
    label: '2+2 with Gov ID International - 2×Address + 2×Name + Gov ID',
    description: '2+2 with Government ID for international',
    region: 'INTL',
    v2ConfigKey: 'two_plus_gov_id',
    requirements: { dataSources: 2, address: 2, dateOfBirth: 0, fullName: 2, govId: 1 }
  },
  {
    key: 'intl_two_plus_gov_id_2',
    label: '2+2 with Gov ID International - Address + 2×Name + Gov ID',
    description: '2+2 with Government ID - No DOB',
    region: 'INTL',
    v2ConfigKey: 'two_plus_gov_id',
    requirements: { dataSources: 2, address: 1, dateOfBirth: 0, fullName: 2, govId: 1 }
  },
  {
    key: 'intl_two_plus_gov_id_3',
    label: '2+2 with Gov ID International - Address + DOB + 2×Name',
    description: '2+2 with Government ID - Balanced (Note: No Gov ID in requirements)',
    region: 'INTL',
    v2ConfigKey: 'two_plus_gov_id',
    requirements: { dataSources: 2, address: 1, dateOfBirth: 1, fullName: 2, govId: 0 }
  },
]

// Simplified V1 KYC Rulesets — V1 uses simple 1+1 or 2+2 verification levels
// Gov ID is a separate add-on check type in V1, not part of the ruleset
export interface V1KycRulesetDefinition {
  key: string
  label: string
  description: string
}

export const V1_KYC_RULESETS: V1KycRulesetDefinition[] = [
  {
    key: 'v1_one_plus_one',
    label: '1+1',
    description: 'Checks name, address and DOB against a minimum of 1 independent data source',
  },
  {
    key: 'v1_two_plus_two',
    label: '2+2',
    description: 'Checks name, address and DOB against a minimum of 2 independent data sources',
  },
  {
    key: 'v1_two_plus_two_name_address',
    label: '2+2 (Name + Address)',
    description: '2+2 verification requiring both name and address matches from 2 independent sources',
  },
  {
    key: 'v1_manual_kyc',
    label: 'Manual KYC',
    description: 'Operator-verified manual identity verification — cannot be combined with 1+1 or 2+2',
  },
]

export interface KycConfig {
  kycRequired: 'yes' | 'no' | 'unknown'
  isReportingEntity: boolean | TriState
  documentTypesAu: string[]
  enableConsentSources: boolean | CreditHeaderCheckMode
  acceptNzCustomers: boolean | TriState
  documentTypesNz: string[]
  dvsForeignPassport: 'dvs' | 'vevo' | 'both' | 'na' | 'unknown' | ''
  internationalCountries: string[]

  auOtherDocumentTypesEnabled: boolean
  auOtherDocumentTypesText: string

  internationalDocumentAcceptance: Record<
    string,
    {
      passport: boolean
      driversLicense: boolean
      otherEnabled: boolean
      otherText: string
    }
  >

  ruleset: string
  customRuleset: string
  vendor: string
  otherVendor: string

  complianceFramework: 'safe_harbour_2x2' | 'standard_1x1' | 'custom' | 'none' | 'unknown'
  specificRegulatoryRequirements: string

  // KYC Ruleset Builder - Support multiple rulesets
  rulesets: Array<{
    id: number
    rulesetKey: string // 'custom' or one of the predefined ruleset keys
    customRequirements?: KycRulesetRequirement
    name?: string // Optional custom name for the ruleset
  }>

  requiredDataElements: {
    fullName: TriState
    middleName: TriState
    dateOfBirth: TriState
    address: TriState
    governmentId: TriState
  }
  minimumDataSources: '1' | '2' | 'custom' | 'unknown'
  minimumDataElementsPerSource: '1' | '2' | 'custom' | 'unknown'

  nameMatchingTolerance: 'exact_100' | 'very_high_95' | 'high_90' | 'standard_85' | 'medium_70_80' | 'tbd'
  dobMatchingTolerance: 'exact' | 'minor_variations' | 'tbd'
  addressMatchingTolerance: 'exact_100' | 'very_high_95' | 'high_90' | 'standard_85' | 'partial' | 'tbd'
  passFailCriteria: string

  primaryCustomerJurisdictions: string[]
  geographicCoverageNotes: string

  auGovernmentSources: {
    dvsDirect: TriState
    dvsViaEquifax: TriState
    vevo: TriState
    electoralRoll: TriState
  }
  creditBureauSources: {
    equifaxCreditHeader: TriState
    equifaxCreditHeaderVia: 'frankieone' | 'direct' | 'unknown'
    equifaxCreditHeaderDirectNote: string
    experianCreditHeader: TriState
    experianCreditHeaderVia: 'frankieone' | 'direct' | 'unknown'
    experianCreditHeaderDirectNote: string
  }
  alternativeAuSources: {
    superchoice: TriState
    superchoiceVia: 'frankieone' | 'direct' | 'unknown'
    superchoiceDirectNote: string
    equifaxCommercialPublicRecords: TriState
    equifaxCommercialPublicRecordsVia: 'frankieone' | 'direct' | 'unknown'
    equifaxCommercialPublicRecordsDirectNote: string
    tenancyDatabase: TriState
    tenancyDatabaseVia: 'frankieone' | 'direct' | 'unknown'
    tenancyDatabaseDirectNote: string
    phoneDirectoryData: TriState
    phoneDirectoryDataVia: 'frankieone' | 'direct' | 'unknown'
    phoneDirectoryDataDirectNote: string
    datazoo: TriState
    datazooVia: 'frankieone' | 'direct' | 'unknown'
    datazooDirectNote: string
  }
  internationalDataSources: {
    gdc: TriState
    gdcVia: 'frankieone' | 'direct' | 'unknown'
    gdcDirectNote: string
    centrix: TriState
    centrixVia: 'frankieone' | 'direct' | 'unknown'
    centrixDirectNote: string
    equifaxNz: TriState
    equifaxNzVia: 'frankieone' | 'direct' | 'unknown'
    equifaxNzDirectNote: string
    asiaVerify: TriState
    asiaVerifyVia: 'frankieone' | 'direct' | 'unknown'
    asiaVerifyDirectNote: string
    monnai: TriState
    monnaiVia: 'frankieone' | 'direct' | 'unknown'
    monnaiDirectNote: string
    otherRegionalProviders: TriState
    otherRegionalProvidersVia: 'frankieone' | 'direct' | 'unknown'
    otherRegionalProvidersDirectNote: string
  }
  dataSourceNotes: string

  enableCascade: 'yes' | 'no' | 'tbd'
  cascadeStrategy: 'strict' | 'balanced' | 'aggressive' | 'custom' | 'tbd'
  sourcePriorityOrder: string[]
  cascadeNotes: string

  manualReviewTriggers: {
    partialMatchesBelowThreshold: TriState
    nameVariationsDetected: TriState
    addressMismatches: TriState
    documentVerificationFailures: TriState
    pepSanctionsHits: TriState
    multipleFailedAttempts: TriState
    highRiskJurisdictions: TriState
    expiredDocuments: TriState
    ageVerificationIssues: TriState
    other: TriState
  }
  manualReviewThreshold: 'below_70' | 'below_75' | 'below_80' | 'below_85' | 'below_90' | 'tbd'
  manualReviewNotes: string

  specialRequirements: string
  configurationStatus: 'fully_defined' | 'partially_defined' | 'early_stage' | 'unknown'
  configurationNotes: string

  // Aging Configuration
  agingEnabled: boolean
  agingPeriodMonths: number
  agingCustomConfig: string

  // Data Sources Configuration (overall list across all workflows)
  selectedDataSources: Array<{
    sourceId: WorkflowCascadeSource // e.g., 'dvs', 'equifax_au', 'gdc_lseg', etc.
    via: 'frankieone' | 'direct' | 'unknown'
    directNote: string // Details when via='direct'
  }>
}

export interface AmlConfig {
  level: 'standard' | 'enhanced' | 'na' | 'unknown' | ''
  pepLevels: string[]
  monitoringType: 'one-time' | 'ongoing' | 'unknown' | ''
  fuzziness: number
  storeInAustralia: boolean
  sendCountriesForPep: boolean
  customBlockList: string
  relationship: 'unknown' | 'frankieone' | 'direct'
  vendor: string
  otherVendor: string

  amlRegulatoryRequirement:
    | 'required_by_regulation'
    | 'business_policy'
    | 'optional_risk_based'
    | 'not_required'
    | 'unknown'

  screeningEntityTypes: {
    individuals: TriState
    businesses: TriState
    ubos: TriState
    directorsAndOfficers: TriState
    authorizedRepresentatives: TriState
  }

  riskBasedScreening: 'yes' | 'no' | 'tbd'

  pepScreeningConfig: {
    enabled: TriState
    includeFamilyMembers: TriState
    includeHistoricalPeps: TriState
  }

  sanctionsScreeningConfig: {
    enabled: TriState
    listsToInclude: Array<
      'all_major'
      | 'ofac'
      | 'un'
      | 'eu'
      | 'uk_hmt'
      | 'dfat'
      | 'country_specific'
      | 'custom'
    >
    customLists: string
    complyAdvanceSanctionIds?: string[]  // ComplyAdvantage specific sanction list IDs
    matchAction: 'automatic_fail' | 'manual_review_required' | 'alert_only' | 'tbd'
  }

  adverseMediaScreeningConfig: {
    enabled: TriState
    categories: Array<
      'financial_crime'
      | 'violent_crime'
      | 'cybercrime'
      | 'regulatory_enforcement'
      | 'corruption'
      | 'human_rights'
      | 'environmental'
      | 'all'
    >
    customCategories: string
    complyAdvanceMediaIds?: string[]  // ComplyAdvantage specific adverse media IDs
    category: 'standard' | 'premium' | 'both' | 'tbd'
    matchAction: 'manual_review_required' | 'alert_only' | 'risk_score_impact' | 'tbd'
  }

  additionalWatchlistsConfig: {
    enabled: TriState
    complyAdvanceWatchlistIds?: string[]  // ComplyAdvantage specific watchlist IDs
    types: Array<
      'law_enforcement'
      | 'regulatory_enforcement'
      | 'disqualified_directors'
      | 'insolvency'
      | 'financial_crime'
      | 'country_specific'
      | 'all'
    >
  }

  ongoingMonitoringEnabled: TriState
  ongoingMonitoringEnrolment: 'automatic' | 'manual_opt_in' | 'unknown' | ''
  ongoingMonitoringFrequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'twice_yearly' | 'custom' | 'tbd'
  ongoingMonitoringScope: Array<'pep' | 'sanctions' | 'adverse_media' | 'watchlists' | 'all'>
  monitoringAutoDisableRules: Array<'account_closed' | 'archived' | 'inactive' | 'low_risk' | 'never' | 'tbd'>
  monitoringAlertHandling:
    | 'automatic_notifications'
    | 'manual_review_queue'
    | 'api_webhooks'
    | 'email_notifications'
    | 'multiple'
    | 'tbd'

  nameMatchingStrategy: {
    includeMiddleNames: 'yes' | 'no' | 'tbd'
    transliterationSupport: 'yes' | 'no' | 'tbd'
  }
  geographicDataEnhancement: 'send_country_data' | 'name_only' | 'tbd'
  dobMatchingConfig: 'exact' | 'year_only' | 'partial' | 'not_used' | 'tbd'
  falsePositiveManagement: Array<'whitelisting' | 'auto_dismiss' | 'manual_review_all' | 'ml_ai' | 'tbd'>

  vendorSelectionRationale: Array<
    'cost_pricing'
    | 'data_coverage'
    | 'accuracy_false_positive_rate'
    | 'ongoing_monitoring'
    | 'integration_api'
    | 'regulatory_acceptance'
    | 'existing_relationship'
    | 'other'
  >
  vendorSpecificFeatures: {
    complyAdvantage: {
      standardPepSanctions: boolean
      addAdverseMedia: boolean
      enableOngoingMonitoring: boolean
      customSearchProfiles: boolean
    }
    refinitiv: {
      worldCheck: boolean
      mediaCheck: boolean
      enableOngoingMonitoring: boolean
      customScreeningGroups: boolean
    }
    dowJones: {
      riskCompliance: boolean
      factiva: boolean
      customWatchlists: boolean
    }
    acuris: {
      pep: boolean
      sanctions: boolean
    }
  }
  multiVendorStrategy: 'single' | 'multiple' | 'regional' | 'not_applicable' | 'tbd'
  vendorRegistrationRequired: TriState

  auditTrailRequirements: Array<'full_audit' | 'reviewer_records' | 'timestamps' | 'retention' | 'export' | 'all'>
  reportingRequirements: Array<'sars' | 'regulatory' | 'internal' | 'board' | 'audit' | 'tbd'>
  suspiciousActivityThresholds: string
  dataRetentionPeriod: '5_years' | '7_years' | '10_years' | 'indefinite' | 'custom' | 'tbd'

  screeningTiming:
    | 'before_account_creation'
    | 'during_onboarding'
    | 'after_onboarding'
    | 'risk_based'
    | 'tbd'
  matchResolutionWorkflow: 'automatic' | 'manual_all' | 'tiered' | 'risk_based' | 'tbd'

  batchProcessing: {
    required: 'unknown' | 'not_required' | 'required_regularly' | 'required_once'
    details: string
  }

  jurisdictionScreening: {
    screenAllJurisdictions: 'yes' | 'no' | 'unknown'
    specificJurisdictions: string
  }

  apiIntegrationRequirements: Array<
    'realtime'
    | 'batch'
    | 'webhooks_matches'
    | 'webhooks_monitoring'
    | 'history_api'
    | 'export_api'
    | 'tbd'
  >
  portalAccessRequirements: Array<'compliance' | 'operations' | 'executive' | 'audit' | 'multi_level' | 'tbd'>

  costOptimizationPriority: 'minimize' | 'balanced' | 'maximize' | 'tbd'
  volumeEstimates: {
    initialScreeningVolume: number | null
    ongoingMonitoringVolume: number | null
    expectedMatchRate: 'low' | 'medium' | 'high' | 'unknown'
  }
  costAllocation: 'pass_through' | 'absorb' | 'hybrid' | 'tbd'

  minorScreeningConfig: 'screen_all' | 'skip_minors' | 'different_rules' | 'tbd'
  deceasedCustomerHandling: 'continue' | 'auto_disable' | 'manual_review' | 'tbd'
  corporateEntityScreening: Array<'company_name' | 'directors' | 'ubos' | 'signatories' | 'shareholders' | 'tbd'>
  highRiskJurisdictionHandling: 'enhanced' | 'block' | 'standard' | 'manual_review' | 'tbd'

  specialRequirements: string
  configurationStatus: 'fully_defined' | 'partially_defined' | 'early_stage' | 'unknown'
  configurationNotes: string
  lastUpdated: string
  updatedBy: string

  // Aging Configuration
  agingEnabled: boolean
  agingPeriodMonths: number
  agingCustomConfig: string
}

export interface BiometricsConfig {
  enabled: TriState
  serviceLevel: 'document_review' | 'document_biometrics_review' | 'unknown' | ''
  flows: IdvFlow[]
  documentTypes: string[]
  smsSettingsConfirmed: TriState
  customSmsNotRequired: boolean
  customSmsMessageTemplate: string
  smsLinkValidityHours: number | null
  frontend: 'hosted_onesdk' | 'embedded_onesdk' | 'none' | 'other' | 'unknown' | ''
  otherFrontend: string
  platform: 'web' | 'in_app' | 'both' | 'unknown' | ''
  vendor: string
  otherVendor: string
  vendorNote: string // Note field for non-FrankieOne vendors
  // Branding requirements
  rebrandingHostedOneSDK: TriState
  rebrandingDaon: TriState
  extendedBranding: TriState
  customRedirectPage: TriState
  customConsents: TriState
  brandingNotes: string
  // Daon engagement (internal only — not exported to PDF or Jira)
  daonEngagementNotes: string
}

export type IdvProvider = 'incode' | 'daon' | 'no_vendor' | 'unknown'

export interface IdvFlow {
  id: string
  name: string
  notes: string
  isDefault: boolean
  idVerificationEnabled: TriState
  idFormEnabled: TriState
  biometricsEnabled: TriState
  reviewScreenEnabled: TriState
  consentScreenEnabled: TriState
  redirectPageEnabled: TriState
  customScreenEnabled: TriState
  customScreenDetails: string
  doubleIdEnabled: TriState
  vendor: IdvProvider
  documentTypes: Array<'passport' | 'drivers_license' | 'medicare_manual' | 'custom' | 'unknown'>
  customDocumentTypeDetails: string
}

export interface KybConfig {
  enabled: TriState

  // Australian Business Checks
  auChecksEnabled: TriState
  auChecks: {
    amlStandard: boolean
    amlEnhanced: boolean
    asicInfo: boolean
    ubo: boolean
    creditReport: boolean
  }

  // KYC for Individuals (replaces Role Verification)
  kycForIndividualsRequired: TriState

  // UBO Consent & Workflow
  uboConsentAware: TriState
  uboSmsPhoneAware: TriState
  uboWorkflowId: string

  // AML Business Entity Screening
  amlEntityScreening: TriState
  amlEntityScreeningTypes: string[]

  // Entity Types — Percentage Split
  entityTypesEnabled: TriState
  entityTypeSplit: {
    soleTradersPercent: number
    ptyLtdPercent: number
    companyPercent: number
    partnershipPercent: number
    trustPercent: number
    otherPercent: number
  }
  otherEntityType: string

  // International KYB — Per-Country Products
  internationalEnabled: TriState
  internationalCountryProducts: Array<{
    country: string
    products: string[]
  }>

  // Trust Analyser — Percentage Split
  trustAnalyserEnabled: TriState
  trustTypeSplit: {
    unitPercent: number
    discretionaryPercent: number
    smsfPercent: number
    otherPercent: number
  }

  // KYB Onboarding Form
  onboardingFormEnabled: TriState
  onboardingFormBranded: TriState

  // KYB AML Ongoing Monitoring
  kybAmlOngoingMonitoringEnabled: TriState
  kybAmlOngoingMonitoringNotes: string

  // Continuous KYB Monitoring (v1 only — CreditorWatch-based)
  continuousKybEnabled: TriState
  continuousKybNotes: string

  // KYB Aging
  agingEnabled: boolean
  agingPeriodMonths: number
  agingCustomConfig: string
}

export const INTERNATIONAL_KYB_PRODUCTS = [
  { id: 'data_retrieval', label: 'Data Retrieval' },
  { id: 'registry_documents', label: 'Registry Documents' },
  { id: 'ubo_verify', label: 'UBO Verify' },
]

export interface Recipe {
  id: number
  name: string
  accountType: string
  products: {
    kyc: boolean
    govId: boolean
    vevo: boolean
    internationalKyc: boolean
    amlStandard: boolean
    amlEnhanced: boolean
    biometrics: boolean
    ccr: boolean
    blocklists: boolean
    duplicates: boolean
    supportDocs: boolean
  }
  ruleset: string
  customRuleset: string
}

export type WorkflowStepType = 'blocklist' | 'gov_id' | 'kyc' | 'idv_biometrics' | 'aml' | 'fraud' | 'risk' | 'death_check' | 'duplicate'

export type WorkflowCascadeSource =
  | 'dvs'
  | 'dvs_government_ids'
  | 'australian_electoral_roll'
  | 'vevo'
  | 'equifax_au'
  | 'australian_tenancy_database'
  | 'equifax_consumer_bureau_credit_header'
  | 'equifax_public_records_data'
  | 'equifax_commercial_bureau'
  | 'fcs_nad_file'
  | 'ipnd'
  | 'scanned_phone_directory'
  | 'experian_au'
  | 'experian_bureau_credit_header'
  | 'superchoice_direct'
  | 'superannuation_data'
  | 'single_touch_payroll_data'
  | 'datazoo_direct'
  | 'au_asic_personal_name'
  | 'au_death_check'
  | 'tica'
  | 'gdc_lseg'
  | 'centrix_nz'
  | 'nz_dia_passport'
  | 'nz_nzta_drivers_license'
  | 'equifax_nz'
  | 'equifax_canada'
  | 'asiaverify_china'
  | 'monnai'
  | 'socure'
  | 'creden_thailand'
  | 'netbay_thailand'
  | 'advance_ai_indonesia'

export const WORKFLOW_CASCADE_SOURCES: Array<{
  id: WorkflowCascadeSource
  label: string
  details: string
  creditHeader?: boolean
  region: 'au' | 'international'
}> = [
  {
    id: 'dvs',
    label: 'DVS',
    details: 'Direct or via Equifax as GSP. Requires customer registration.',
    region: 'au',
  },
  {
    id: 'dvs_government_ids',
    label: 'DVS Government IDs',
    details:
      "Driver's License (all states), Australian Passport, Medicare, Foreign Passport with visa, ImmiCard, Citizenship Certificate, Centrelink Concession Card, Birth Certificate, Change of Name, Marriage Certificate.",
    region: 'au',
  },
  {
    id: 'australian_electoral_roll',
    label: 'Australian Electoral Roll',
    details: 'Accessed via DVS; counted separately.',
    region: 'au',
  },
  {
    id: 'vevo',
    label: 'VEVO',
    details: 'Visa checks. Can be accessed direct or through NCC.',
    region: 'au',
  },
  {
    id: 'equifax_au',
    label: 'Equifax AU',
    details: 'Requires customer registration per customer/company.',
    region: 'au',
  },
  {
    id: 'australian_tenancy_database',
    label: 'Australian Tenancy Database',
    details: 'Tenancy database (AU).',
    region: 'au',
  },
  {
    id: 'equifax_consumer_bureau_credit_header',
    label: 'Equifax Consumer Bureau Credit Header',
    details: 'Credit Header. Consent required.',
    creditHeader: true,
    region: 'au',
  },
  {
    id: 'equifax_public_records_data',
    label: 'Equifax Public Records Data',
    details: 'Public records data.',
    region: 'au',
  },
  {
    id: 'equifax_commercial_bureau',
    label: 'Equifax Commercial Bureau',
    details: 'Commercial bureau data.',
    region: 'au',
  },
  {
    id: 'fcs_nad_file',
    label: 'FCS NAD File',
    details: 'Historical Electoral Roll (1997-2004).',
    region: 'au',
  },
  {
    id: 'ipnd',
    label: 'IPND',
    details: 'Integrated Public Number Directory.',
    region: 'au',
  },
  {
    id: 'scanned_phone_directory',
    label: 'Scanned Phone Directory',
    details: 'Scanned phone directory.',
    region: 'au',
  },
  {
    id: 'experian_au',
    label: 'Experian AU',
    details: 'Requires customer registration per customer/company.',
    region: 'au',
  },
  {
    id: 'experian_bureau_credit_header',
    label: 'Experian Bureau Credit Header',
    details: 'Credit Header. Consent required.',
    creditHeader: true,
    region: 'au',
  },
  {
    id: 'superchoice_direct',
    label: 'Superchoice (Direct)',
    details: 'Exclusive to FrankieOne.',
    region: 'au',
  },
  {
    id: 'superannuation_data',
    label: 'Superannuation Data',
    details: 'Superannuation data.',
    region: 'au',
  },
  {
    id: 'single_touch_payroll_data',
    label: 'Single Touch Payroll Data',
    details: 'Payroll data.',
    region: 'au',
  },
  {
    id: 'datazoo_direct',
    label: 'DataZoo (Direct)',
    details: 'Direct integration.',
    region: 'au',
  },
  {
    id: 'au_asic_personal_name',
    label: 'AU ASIC Personal Name',
    details: 'ASIC personal name source.',
    region: 'au',
  },
  {
    id: 'au_death_check',
    label: 'AU Death Check',
    details: 'Australian death check.',
    region: 'au',
  },
  {
    id: 'tica',
    label: 'TICA',
    details: 'Tenancy Information and Compliance Authority.',
    region: 'au',
  },
  {
    id: 'gdc_lseg',
    label: 'GDC / LSEG',
    details:
      'Global Data Consortium (Direct). 120+ countries. Consent required: Canada only (HK no longer available as of 2025).',
    region: 'international',
  },
  {
    id: 'centrix_nz',
    label: 'Centrix NZ',
    details: 'Requires customer registration per customer/company.',
    region: 'international',
  },
  {
    id: 'nz_dia_passport',
    label: 'NZ Government - DIA (Passport)',
    details: 'NZ Passport.',
    region: 'international',
  },
  {
    id: 'nz_nzta_drivers_license',
    label: "NZ Government - NZTA (Driver's License)",
    details: 'NZ Driver’s License.',
    region: 'international',
  },
  {
    id: 'equifax_nz',
    label: 'Equifax NZ',
    details: 'Requires customer registration per customer/company. Connects to DIA (NZ Passport) and NZTA (NZ Driver’s License).',
    region: 'international',
  },
  {
    id: 'equifax_canada',
    label: 'Equifax Canada',
    details: 'Requires customer registration per customer/company. AML Assist for FINTRAC (single credit file method).',
    region: 'international',
  },
  {
    id: 'asiaverify_china',
    label: 'AsiaVerify (China)',
    details: 'Direct. Chinese National ID, Passport, Driver’s License. Also other SEA countries and KYB capabilities.',
    region: 'international',
  },
  {
    id: 'monnai',
    label: 'Monnai',
    details: 'Direct. India: passport verification (requires passport number + file number). Philippines: passport verification.',
    region: 'international',
  },
  {
    id: 'socure',
    label: 'Socure',
    details: 'Requires customer registration per customer/company. Mainly used for US and UK data sources.',
    region: 'international',
  },
  {
    id: 'creden_thailand',
    label: 'Creden (Thailand)',
    details: 'Direct. Requires prepaid credit. LED bankruptcy check, DOPA National ID, AMLO (PEP & Sanctions).',
    region: 'international',
  },
  {
    id: 'netbay_thailand',
    label: 'Netbay (Thailand)',
    details: 'Direct. Thai data sources connector.',
    region: 'international',
  },
  {
    id: 'advance_ai_indonesia',
    label: 'Advance AI (Indonesia)',
    details:
      'Direct. Requires prepaid. Indonesia National ID (KTP) with Dukcapil DB. Customer needs registered business entity in Indonesia.',
    region: 'international',
  },
]

export type RiskBranchOutcome = 'pass' | 'fail' | 'in_review' | ''

export type RiskBranch = {
  id: string
  condition: string
  outcome: RiskBranchOutcome
  steps: WorkflowStep[]
}

export type WorkflowStep =
  | {
      id: string
      type: 'blocklist'
    }
  | {
      id: string
      type: 'gov_id'
    }
  | {
      id: string
      type: 'kyc'
      rulesetId: string
      cascadeSources: WorkflowCascadeSource[]
    }
  | {
      id: string
      type: 'idv_biometrics'
      flowId: string | null
      requirement: 'mandatory' | 'optional'
    }
  | {
      id: string
      type: 'aml'
    }
  | {
      id: string
      type: 'fraud'
    }
  | {
      id: string
      type: 'risk'
      branches: RiskBranch[]
    }
  | {
      id: string
      type: 'death_check'
    }
  | {
      id: string
      type: 'duplicate'
      notes: string
    }

// ── V1 Recipe Definitions ──────────────────────────────────────────────────
// In v1, workflows are configured via recipes (pre-configured profiles) rather
// than the step-by-step builder used in v2.

export type V1RecipeKey =
  | 'aml_only'
  | 'aml_media_only'
  | 'default'
  | 'gov_id'
  | 'gov_id_media'
  | 'international'
  | 'international_media'
  | 'organisation'
  | 'safe_harbour'
  | 'safe_harbour_id'
  | 'safe_harbour_id_media'
  | 'safe_harbour_media'
  | 'safe_harbour_plus'
  | 'safe_harbour_plus_media'
  | 'safe_harbour_two_id'

export interface V1RecipeDefinition {
  key: V1RecipeKey
  label: string
  description: string
  amlLevel: 'standard' | 'enhanced'
  checkTypes: string[]  // portal check types (e.g. 'Blocklist', '2+2', 'PEP / Sanctions')
  checkOrder: string[]  // portal check order (e.g. 'Blocklist', 'Identity', 'Entity', 'AML')
}

export const V1_RECIPES: V1RecipeDefinition[] = [
  {
    key: 'aml_media_only',
    label: 'Enhanced AML only',
    description: 'Enhanced AML only',
    amlLevel: 'enhanced',
    checkTypes: ['Blocklist', 'PEP / Sanctions / Media'],
    checkOrder: ['Blocklist', 'AML'],
  },
  {
    key: 'aml_only',
    label: 'Standard AML only',
    description: 'Standard AML only',
    amlLevel: 'standard',
    checkTypes: ['Blocklist', 'PEP / Sanctions'],
    checkOrder: ['Blocklist', 'AML'],
  },
  {
    key: 'default',
    label: 'Default',
    description: 'Default',
    amlLevel: 'standard',
    checkTypes: ['2+2', 'Gov ID', 'PEP / Sanctions'],
    checkOrder: ['Entity', 'Identity', 'AML', 'Fraud'],
  },
  {
    key: 'gov_id',
    label: 'Gov ID',
    description: 'Gov ID',
    amlLevel: 'standard',
    checkTypes: ['Blocklist', 'Gov ID Only', 'PEP / Sanctions', 'ID Validation'],
    checkOrder: ['Blocklist', 'Identity', 'Entity', 'ID Validation', 'AML'],
  },
  {
    key: 'gov_id_media',
    label: 'Gov ID & Media',
    description: 'Gov ID & Media',
    amlLevel: 'enhanced',
    checkTypes: ['Blocklist', 'Gov ID Only', 'PEP / Sanctions / Media', 'ID Validation'],
    checkOrder: ['Blocklist', 'Identity', 'Entity', 'ID Validation', 'AML'],
  },
  {
    key: 'international',
    label: 'International',
    description: 'International',
    amlLevel: 'standard',
    checkTypes: ['Blocklist', '1+1', 'PEP / Sanctions', 'ID Validation'],
    checkOrder: ['Blocklist', 'Identity', 'Entity', 'ID Validation', 'AML'],
  },
  {
    key: 'international_media',
    label: 'International & Media',
    description: 'International & Media',
    amlLevel: 'enhanced',
    checkTypes: ['Blocklist', '1+1', 'PEP / Sanctions / Media', 'ID Validation'],
    checkOrder: ['Blocklist', 'Identity', 'Entity', 'ID Validation', 'AML'],
  },
  {
    key: 'organisation',
    label: 'Business',
    description: 'Business',
    amlLevel: 'standard',
    checkTypes: ['PEP / Sanctions'],
    checkOrder: ['AML'],
  },
  {
    key: 'safe_harbour',
    label: 'Standard Safe Harbour',
    description: 'Standard Safe Harbour',
    amlLevel: 'standard',
    checkTypes: ['Blocklist', '2+2', 'PEP / Sanctions', 'ID Validation'],
    checkOrder: ['Blocklist', 'Identity', 'Entity', 'ID Validation', 'AML'],
  },
  {
    key: 'safe_harbour_id',
    label: 'Safe Harbour & ID',
    description: 'Safe Harbour & ID',
    amlLevel: 'standard',
    checkTypes: ['Blocklist', '2+2', 'Gov ID', 'PEP / Sanctions', 'ID Validation'],
    checkOrder: ['Blocklist', 'Identity', 'Entity', 'ID Validation', 'AML'],
  },
  {
    key: 'safe_harbour_id_media',
    label: 'Safe Harbour, ID & Media',
    description: 'Safe Harbour, ID & Media',
    amlLevel: 'enhanced',
    checkTypes: ['Blocklist', '2+2', 'Gov ID', 'PEP / Sanctions / Media', 'ID Validation'],
    checkOrder: ['Blocklist', 'Identity', 'Entity', 'ID Validation', 'AML'],
  },
  {
    key: 'safe_harbour_media',
    label: 'Standard Safe Harbour & Media',
    description: 'Standard Safe Harbour & Media',
    amlLevel: 'enhanced',
    checkTypes: ['Blocklist', '2+2', 'PEP / Sanctions / Media', 'ID Validation'],
    checkOrder: ['Blocklist', 'Identity', 'Entity', 'ID Validation', 'AML'],
  },
  {
    key: 'safe_harbour_plus',
    label: 'Safe Harbour, ID & Address',
    description: 'Safe Harbour, ID & Address',
    amlLevel: 'standard',
    checkTypes: ['Blocklist', '2+2 (Name + Address)', 'Gov ID', 'PEP / Sanctions', 'ID Validation'],
    checkOrder: ['Blocklist', 'Identity', 'Entity', 'ID Validation', 'AML'],
  },
  {
    key: 'safe_harbour_plus_media',
    label: 'Safe Harbour, ID, Address & Media',
    description: 'Safe Harbour, ID, Address & Media',
    amlLevel: 'enhanced',
    checkTypes: ['Blocklist', '2+2 (Name + Address)', 'Gov ID', 'PEP / Sanctions / Media', 'ID Validation'],
    checkOrder: ['Blocklist', 'Identity', 'Entity', 'ID Validation', 'AML'],
  },
  {
    key: 'safe_harbour_two_id',
    label: 'Safe Harbour & 2 IDs',
    description: 'Safe Harbour & 2 IDs',
    amlLevel: 'standard',
    checkTypes: ['2+2', 'id_two_govid', 'PEP / Sanctions', 'ID Validation'],
    checkOrder: ['Identity', 'Entity', 'ID Validation', 'AML'],
  },
]

// ── V1 Custom Profile Check Types ────────────────────────────────────────────
// Available check types when creating a custom V1 profile. Each check can be
// configured with stop-on-fail and optional flags.

export const V1_CHECK_TYPE_OPTIONS = [
  { id: 'one_plus_one', label: '1+1', description: 'Checks name, address and DoB against a minimum of 1 data source' },
  { id: 'two_plus_two_name_address', label: '2+2 (Name + Address)', description: '2+2 verification requiring both name and address matches from independent sources' },
  { id: 'one_plus_one_test', label: '1+1 Test', description: 'Connector testing profile for 1+1 verification' },
  { id: 'two_plus_two_test', label: '2+2 Test', description: 'Connector testing profile for 2+2 verification' },
  { id: 'gov_id', label: 'Gov ID', description: 'Checks all identity documents; should be used with 1+1 or 2+2 for full coverage' },
  { id: 'gov_id_only', label: 'Gov ID Only', description: 'Identity document check without additional entity verification' },
  { id: 'name_validation', label: 'Name Validation', description: 'Validates name transliteration for entities with national IDs' },
  { id: 'visa', label: 'Visa', description: 'Validates Australian visa documents, class and subclass information' },
  { id: 'pep_sanctions_media', label: 'PEP / Sanctions / Media', description: 'PEP/sanctions screening plus watchlist and adverse media checks' },
  { id: 'blocklist', label: 'Blocklist', description: 'Checks entity against internal blocklists' },
  { id: 'duplicate', label: 'Duplicate', description: 'Checks for duplicate/matching entities in the system' },
  { id: 'manual_kyc', label: 'Manual KYC', description: 'Operator-verified manual identity verification; cannot combine with 1+1 or 2+2' },
  { id: 'check_source', label: 'Check Source', description: 'Validates and translates check source common names' },
  { id: 'fraud_list', label: 'Fraud List', description: 'Checks if identity appears on known fraud lists' },
  { id: 'fraud_detection', label: 'Fraud Detection', description: 'Checks external services (e.g. EmailAge) for fraud indicators' },
  { id: 'transactions', label: 'Transactions', description: 'Transaction monitoring and device-based fraud indicators' },
  { id: 'aml_update', label: 'AML Update', description: 'Triggers an update of existing AML screening results' },
] as const

export type V1CustomCheck = {
  id: string
  checkTypeId: string
  stopOnFail: boolean
  optional: boolean
}

export type BuilderWorkflow = {
  kind?: 'builder'
  id: string
  name: string
  notes: string
  associatedFlowId: string | null
  steps: WorkflowStep[]
  v1RecipeKey?: string
  v1CustomChecks?: V1CustomCheck[]
}

export type ComplexWorkflow = {
  kind: 'complex'
  id: string
  name: string
  workflowId: string
  url: string
  notes: string
}

export type Workflow = BuilderWorkflow | ComplexWorkflow

export type RiskRatingSystem = 'frankieone' | 'theirs' | 'both' | 'unknown'

export type RiskSystemChange = 'changing' | 'not_changing' | 'unknown'

export type DuplicateMatchField =
  | 'EXTERNAL_REFERENCE'
  | 'DOC_PRIMARY_IDENTIFIER'
  | 'DOC_SECONDARY_IDENTIFIER'
  | 'DOC_COUNTRY'
  | 'DOC_SUBDIVISION'
  | 'DOC_TYPE'
  | 'PHONE_NUMBER'
  | 'EMAIL_ADDRESS'
  | 'GIVEN_NAME'
  | 'FAMILY_NAME'
  | 'DATE_OF_BIRTH'
  | 'ADDR_NORM_SHORT'
  | 'ADDR_NORM_LONG'

export type DuplicateMatchMethod = 'EXACT' | 'FUZZY'

export type DuplicateMatchStrength = 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH'

export interface DuplicateRule {
  id: string
  name: string
  matchFields: DuplicateMatchField[]
  method: DuplicateMatchMethod
  strength: DuplicateMatchStrength
}

export type BlocklistDataType =
  | 'EMAIL'
  | 'EMAIL_DOMAIN'
  | 'PHONE_NUMBER'
  | 'IP_ADDRESS'
  | 'GIVEN_NAME'
  | 'FAMILY_NAME'
  | 'FULL_NAME'
  | 'COUNTRY'
  | 'DOC_ID_NUMBER'
  | 'EXTERNAL_REFERENCE'
  | 'DEVICE_ID'
  | 'OTHER'

export type BlocklistAction = 'BLOCK' | 'ALLOW' | 'ALERT' | 'REVIEW' | 'NONE'

export interface BlocklistEntry {
  id: string
  name: string
  dataType: BlocklistDataType
  action: BlocklistAction
  riskScore: number | null
  description: string
}

export interface RulesConfig {
  duplicateRules: 'default' | 'custom' | 'unknown' | 'not_required' | ''
  customDuplicateRules: string
  customDuplicateRuleSet: DuplicateRule[]
  customDuplicateNotes: string
  blocklistRules: 'default' | 'custom' | 'unknown' | 'not_required' | '' // 'default' is deprecated - no default blocklist rules exist
  customBlocklistRules: string
  customBlocklistEntries: BlocklistEntry[]
  customBlocklistNotes: string

  riskRatingSystem: RiskRatingSystem
  riskSystemChange: RiskSystemChange

  fraudEnabled: TriState
  fraudTypes: string[]
  transactionMonitoringEnabled: TriState

  fraudRequirements: string
  transactionMonitoringRequirements: string
}

export type RiskLevelId = 'unacceptable' | 'high' | 'medium' | 'low' | 'unknown_unchecked'

export interface RiskLevelRule {
  id: RiskLevelId
  label: string
  threshold: number
  ratingMin: number
  ratingMax: number | null
  statusResponse: string
}

export interface IndividualRiskModelRow {
  factor: string
  outcome: string
  rating: number
}

export interface RiskScoringMatrixRow {
  factorGroup: string
  reviewFactor: string
  criteria: string
  pointsAllocated: number
}

export type CustomRiskScoringMethod = 'lookup' | 'truth' | 'range' | 'direct'

export type CustomRiskKvpType = 'general.string' | 'general.integer' | 'general.float' | 'general.bool'

export type CustomRiskAppliesTo = 'individual' | 'business' | 'both'

export interface CustomRiskLookupMappingRow {
  value: string
  score: number
}

export interface CustomRiskRangeMappingRow {
  range: string
  score: number
}

export interface CustomRiskFactor {
  id: string
  name: string
  description: string

  scoringMethod: CustomRiskScoringMethod
  kvpType: CustomRiskKvpType

  includeZeroScores: boolean
  defaultValue: string
  defaultScore: number
  appliesTo: CustomRiskAppliesTo

  kvpKeyOverride: string
  portalVisible: boolean

  lookupMappings: CustomRiskLookupMappingRow[]
  rangeMappings: CustomRiskRangeMappingRow[]
  truthyScore: number
  falsyScore: number
}

export interface RiskConfig {
  riskLevels: RiskLevelRule[]
  individualModel: IndividualRiskModelRow[]
  scoringMatrix: RiskScoringMatrixRow[]
  customFactors: CustomRiskFactor[]
  notes: string
  customRiskSheetUrl: string
}

export interface ScopeHour {
  id: number
  section: string
  description: string
  estimatedHours: number
  complexity: 'low' | 'medium' | 'high' | 'very_high'
}

export interface FeatureRequest {
  id: number
  name: string
  description: string
  hours: number
}

// SOW Configuration Types

/** @deprecated No longer used in SowConfig. Kept for backward compatibility with persisted data. */
export type DeliveryMethodology = 'agile' | 'waterfall' | 'hybrid' | 'unknown'

export type RaciValue = 'R' | 'A' | 'C' | 'I' | ''
export type GanttParty = 'client' | 'frankieone'

export interface RaciColumn {
  id: string
  name: string  // e.g., "FrankieOne PM", "Client Tech Lead"
}

export interface RaciEntry {
  id: number
  task: string
  assignments: Record<string, RaciValue>  // keyed by RaciColumn.id
}

export interface SowMilestone {
  id: number
  name: string
  date: string
  description: string
}

export interface SowPhase {
  id: number
  name: string
  description: string
  duration: string  // e.g., "4 weeks"
  startDate: string
  endDate: string
  deliverables: string[]
}

export interface SowActivity {
  id: number
  phaseId: number
  phaseName: string
  activityName: string
  description: string
  outcome: string
  deliverables: string[]
  deliveryDate: string
}

export interface GanttTask {
  id: number
  phaseId: number
  taskName: string
  startDate: string
  endDate: string
  dependencies: string[]  // IDs of dependent tasks
  resources: string[]     // Names of assigned resources
  responsiblePerson: string
  party: GanttParty | ''
}

export type SowPricingModel = 'fixed' | 'tm' | ''

export interface SowConfig {
  // Legal Text (SOW preamble & scope of services template)
  legalText: string

  // Delivery Notes
  deliveryNotes: string

  // Phases & Milestones
  milestones: SowMilestone[]
  phases: SowPhase[]

  // Commercials
  commercialsPricingModel: SowPricingModel
  commercialsFixedFee: string       // AUD amount for fixed-fee model
  commercialsTmRate: string         // AUD rate per hour for T&M model
  commercialsTmCap: string          // AUD total cap for T&M model

  // RACI Chart
  raciColumns: RaciColumn[]
  raciEntries: RaciEntry[]

  // Gantt Chart Data
  ganttTasks: GanttTask[]

  // Activities Table (legacy, kept for backward compat)
  activities: SowActivity[]

  // Key Assumptions (free-text section listing scope-specific assumptions)
  keyAssumptions: string

  // Integration Sequence Diagram (DSL text for per-deal flow diagram)
  sequenceDiagramDsl: string

  // Additional notes
  notes: string

  // Implementations manager assignment
  implementationsManager: string
}

// --- Blueprint types ---

export interface BlueprintCurrentVendor {
  id: number
  function: 'kyc' | 'aml' | 'idv' | 'fraud' | 'risk' | 'device' | 'monitoring' | 'other'
  vendorName: string
  notes: string
}

export type SignalStatus = 'captured' | 'partial' | 'missing' | 'unknown'

export interface BlueprintSignalEntry {
  id: number
  signalType: string
  geo: string
  status: SignalStatus
  notes: string
}

export interface BlueprintStakeholder {
  id: number
  name: string
  role: string
  technicalConcerns: string
  engagementState: 'evaluator' | 'champion_candidate' | 'champion' | 'blocker' | 'unknown'
}

export interface BlueprintPostCallUpdate {
  id: number
  date: string
  callWith: string
  hypothesisChanges: string
  gapsRemaining: string
  nextCallProbes: string
}

export type BlueprintRuleset = '1+1' | '2+2' | 'custom' | 'unknown'
export type BlueprintFuzziness = 'strict' | 'moderate' | 'fuzzy' | 'optional' | 'unknown'

export interface BlueprintDataInput {
  type: string
  enabled: boolean
  confirmed: boolean
  fuzziness: BlueprintFuzziness
  captureMethod: string
  addressFormat: string
  notes: string
}

export interface BlueprintCountryRuleset {
  id: number
  country: string
  dataInputs: BlueprintDataInput[]
  ruleset: BlueprintRuleset
  govIdRequired: boolean
  nationalIds: string[]
  nationalIdNotes: string
  docScanVendors: string[]
  selfieVendors: string[]
  deviceDataVendors: string[]
  passRate: string
  passRateConfirmed: boolean
  desiredPassRate: string
  desiredPassRateConfirmed: boolean
  notes: string
}

export interface BlueprintPrepCheck {
  id: string
  label: string
  checked: boolean
}

export interface BlueprintVerificationLayer {
  id: string
  layer: string
  customLabel: string  // only used when layer === 'other'
  vendor: string
  owner: string
  painPoints: string
}

export type BlueprintMaturityStage = 0 | 1 | 2 | 3 | 4

export interface BlueprintMaturityDimension {
  id: string
  dimension: string
  stage: BlueprintMaturityStage
  evidence: string
}

export interface BlueprintPriorityGap {
  id: number
  rank: number
  description: string
  impact: string
}

export interface BlueprintRecommendedAction {
  id: number
  text: string
}

export interface BlueprintConfig {
  // Context
  businessModel: string
  monthlyOnboardingVolume: string
  strategicPriorities: string
  strategicPrioritiesSummary: string

  // Current Metrics
  stpRate: string
  stpRateConfirmed: boolean
  fraudRate: string
  fraudRateConfirmed: boolean

  // Customer Problem Hypothesis
  problemHypothesis: string
  problemHypothesisSummary: string
  surfacePain: string
  surfacePainSummary: string
  deepPain: string
  deepPainSummary: string
  whyNow: string
  whyNowSummary: string

  // Regulatory & Industry Context
  regulatoryContext: string
  regulatoryContextSummary: string
  regulatoryIntel: string

  // As-Is Architecture
  currentProcess: string
  currentProcessSummary: string
  miroLink: string
  currentVendors: BlueprintCurrentVendor[]
  integrationMethod: string
  jurisdictionsInScope: string
  onboardingJourney: string
  checksBeforeTransact: string
  riskSegmentation: string
  eddTriggers: string
  sanctionsPepScreening: string
  ongoingMonitoring: string
  frictionFalsePositives: string

  // Verification Stack
  verificationStack: BlueprintVerificationLayer[]

  // Signal Map
  signals: BlueprintSignalEntry[]

  // Country Rulesets
  countryRulesets: BlueprintCountryRuleset[]

  // Maturity Assessment
  maturityDimensions: BlueprintMaturityDimension[]

  // Priority Gaps
  priorityGaps: BlueprintPriorityGap[]

  // Recommended Path
  quickWins: BlueprintRecommendedAction[]
  foundationMoves: BlueprintRecommendedAction[]
  strategicShifts: BlueprintRecommendedAction[]

  // Technical Stakeholder Map
  stakeholders: BlueprintStakeholder[]

  // Post-Call Hypothesis Updates
  postCallUpdates: BlueprintPostCallUpdate[]

  // Preparation Checklist
  prepChecklist: BlueprintPrepCheck[]
}

export interface ScopeData {
  customerName: string
  customerAbn: string
  hubspotDealId: string | null
  hubspotDealName: string | null
  generalInfo: GeneralInfo
  blueprintConfig: BlueprintConfig
  kycConfig: KycConfig
  amlConfig: AmlConfig
  biometricsConfig: BiometricsConfig
  kybConfig: KybConfig
  recipesConfig: Recipe[]
  workflowsConfig: Workflow[]
  rulesConfig: RulesConfig
  riskConfig: RiskConfig
  sowConfig: SowConfig
  sowCustomizedSections: string[]
  scopeHours: ScopeHour[]
  featureRequests: FeatureRequest[]
  workflowsNotes: string
  workflowsRequiredCount: number | null
  notes: string
  finalized: boolean
  finalizedAt: string | null
  finalizedBy: string | null
}

// Document type options (DVS-verifiable only)
export const AU_DOCUMENT_TYPES = [
  { id: 'passport', label: 'Passport' },
  { id: 'drivers_license', label: "Driver's License" },
  { id: 'medicare', label: 'Medicare Card' },
  { id: 'foreign_passport', label: 'Foreign Passport (Visa)' },
  { id: 'birth_certificate', label: 'Birth Certificate' },
]

export const AU_OTHER_DOCUMENT_TYPES = [
  { id: 'citizenship_certificate', label: 'Citizenship Certificate' },
  { id: 'immicard', label: 'ImmiCard (Immigration Card)' },
  { id: 'marriage_certificate', label: 'Marriage Certificate' },
  { id: 'name_change_certificate', label: 'Name Change Certificate' },
  { id: 'death_certificate', label: 'Death Certificate' },
  { id: 'pensioner_concession_card', label: 'Pensioner Concession Card' },
  { id: 'health_care_card', label: 'Health Care Card' },
  { id: 'commonwealth_seniors_health_card', label: 'Commonwealth Seniors Health Card' },
  { id: 'centrelink_other', label: 'Other Centrelink cards (Concession/Pension/Health Concession)' },
]

export const NZ_DOCUMENT_TYPES = [
  { id: 'passport', label: 'Passport' },
  { id: 'drivers_license', label: "Driver's License" },
  { id: 'birth_certificate', label: 'Birth Certificate' },
]

export const KYC_VENDORS = [
  { id: 'unknown', label: "Don't know" },
  { id: 'frankieone', label: 'FrankieOne' },
  { id: 'gbg', label: 'GBG' },
  { id: 'illion', label: 'Illion' },
  { id: 'equifax', label: 'Equifax' },
  { id: 'other', label: 'Other' },
]

export const AML_VENDORS = [
  { id: 'comply', label: 'ComplyAdvantage' },
  { id: 'refinitiv', label: 'Refinitiv WorldCheck' },
  { id: 'other', label: 'Other' },
]

export const AML_RELATIONSHIPS = [
  { id: 'unknown', label: "Don't know" },
  { id: 'frankieone', label: 'FrankieOne' },
  { id: 'direct', label: 'Direct' },
] as const

export const IDV_VENDORS = [
  { id: 'frankieone', label: 'FrankieOne' },
  { id: 'daon', label: 'Daon' },
  { id: 'onfido', label: 'Onfido' },
  { id: 'jumio', label: 'Jumio' },
  { id: 'other', label: 'Other' },
]

export const BIOMETRICS_DOCUMENT_TYPES = [
  { id: 'drivers_license', label: "Driver's License" },
  { id: 'passport', label: 'Passport' },
  { id: 'medicare_manual', label: 'Medicare card (Manual input)' },
]

export const COMMON_COUNTRIES = [
  'Australia',
  'New Zealand',
  'United Kingdom',
  'United States',
  'Canada',
  'Singapore',
  'Hong Kong',
  'India',
  'Philippines',
  'Malaysia',
  'Indonesia',
  'Thailand',
  'Vietnam',
  'Japan',
]

export const ENTITY_TYPES = [
  { id: 'sole_trader', label: 'Sole Trader' },
  { id: 'pty_ltd', label: 'PTY LTD' },
  { id: 'company', label: 'Company' },
  { id: 'partnership', label: 'Partnership' },
  { id: 'trust', label: 'Trust' },
  { id: 'other', label: 'Other' },
]

export const HOUR_SECTIONS = [
  { id: 'general_setup', label: 'General Setup' },
  { id: 'kyc', label: 'KYC Configuration' },
  { id: 'aml', label: 'AML Configuration' },
  { id: 'biometrics', label: 'Biometrics/IDV' },
  { id: 'kyb', label: 'KYB Configuration' },
  { id: 'workflows', label: 'Workflows' },
  { id: 'recipes', label: 'Recipes & Rulesets' },
  { id: 'risk', label: 'Risk Configuration' },
  { id: 'fraud_txm', label: 'Fraud & Transaction Monitoring' },
  { id: 'integration', label: 'Integration' },
  { id: 'custom_work', label: 'Custom Work' },
  { id: 'testing', label: 'Testing' },
  { id: 'training', label: 'Training' },
  { id: 'other', label: 'Other' },
]

export const COMPLEXITY_OPTIONS = [
  { id: 'low', label: 'Low', multiplier: 1 },
  { id: 'medium', label: 'Medium', multiplier: 1.5 },
  { id: 'high', label: 'High', multiplier: 2 },
  { id: 'very_high', label: 'Very High', multiplier: 3 },
]

// ==================== Scope Versioning ====================

export interface ScopeVersion {
  id: number
  deal_id: number
  version_number: number
  version_label: string | null
  version_type: 'snapshot' | 'ai_proposal' | 'active'
  proposal_status: 'pending' | 'accepted' | 'rejected' | 'modified' | null
  ai_source: string | null
  ai_confidence_notes: string | null
  total_hours: number
  total_cost: number
  created_by: string | null
  created_by_name: string | null
  created_at: string
  reviewed_by: string | null
  reviewed_at: string | null
}

export interface ScopeVersionDetail extends ScopeVersion {
  general_info: string
  kyc_config: string
  aml_config: string
  biometrics_config: string
  kyb_config: string
  recipes_config: string
  rules_config: string
  risk_scoring_config: string
  feature_requests: string
  portal_users: string
  technical_contact: string
  sow_config: string
  notes: string | null
  ai_prompt_context: string | null
  review_notes: string | null
}
